import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext } from 'formik';
import { merge } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import SubOrgMultiSelectWithChipsFormik from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import TooltipIcon from '~/components/core/TooltipIcon';
import { InfoIcon } from '~/components/icons';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { reportAxiosError } from '~/Utils';

import { DATA_EXPORT } from '../../../Types';
import CardDialog from '../../CardDialog';
import mixpanel from '../../CmsMain/mixpanel';
import AlertBanner from '../../core/AlertBanner';
import { TextFieldFormik } from '../../TextFieldFormik';
import { useSysconfig } from '../SystemConfigurationScreen';

import ScheduleDetailsSection from './shared/ScheduleDetailsSection';
import TargetConfigurationSection from './shared/TargetConfigurationSection';
import { NON_CLAIM_COMMUNICATION_ATTENTION_NOTE } from './consts.json';

import styles from './ScheduledExportDialog.module.scss';

const DEFAULT_SECRET_TEMPLATE = '*****';
const SECRET_JSON_FIELDS = [
  'type',
  'project_id',
  'private_key_id',
  'private_key',
  'client_email',
  'client_id',
  'auth_uri',
  'token_uri',
  'auth_provider_x509_cert_url',
  'client_x509_cert_url',
];

const ScheduledExportDialog = ({ onClose, onSubmit, scheduledExports, rowIDToEdit }) => {
  const { organization } = useSysconfig();
  const scheduleExportToEdit = scheduledExports.find((row) => row.id === rowIDToEdit);

  let initialValues = {
    display_name: '',
    file_name_prefix: '',
    sub_organization_ids: [],
    target_type: 'email',
    user_id: undefined,
    export_format: DATA_EXPORT.EXPORT_FORMAT.csv.key,
    export_hour: '',
    end_date: undefined,
    is_active: true,
    include_non_claim_communications: false,
    include_email_communication_content: false,
    should_use_pgp_encryption: false,
    pgp_encryption_public_key: '',
    export_days: [],
    target_configurations: {
      s3_bucket: {
        path: '',
        bucket_name: '',
        access_key_id: '',
        access_key_secret: '',
      },
      gcs_bucket: {
        path: '',
        bucket_name: '',
        secret_json: '',
      },
      sftp: {
        host: '',
        port: '',
        path: '',
        user: '',
        password: '',
      },
    },
  };

  const serverExportHourToClient = (serverExportHour) => (serverExportHour ? serverExportHour.substring(0, 5) : '');

  if (scheduleExportToEdit) {
    initialValues = merge(initialValues, scheduleExportToEdit);
    initialValues.export_hour = serverExportHourToClient(scheduleExportToEdit.export_hour);
    initialValues.is_end_date = !!scheduleExportToEdit.end_date;
    if (scheduleExportToEdit.is_all_sub_orgs) {
      initialValues.sub_organization_ids = [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id];
    }
  }

  const getDisplayNameValidation = (displayName) => {
    const displayNameExists = scheduledExports.map((item) => item.display_name).includes(displayName);
    let isValid = false;

    if (scheduleExportToEdit) {
      isValid = scheduleExportToEdit.display_name === displayName || !displayNameExists;
    } else {
      isValid = !displayNameExists;
    }
    return isValid;
  };

  const getSecretJsonValidation = (secretJsonStr) => {
    try {
      if (secretJsonStr === DEFAULT_SECRET_TEMPLATE) {
        return true;
      }

      const secretJsonKeys = Object.keys(JSON.parse(secretJsonStr));
      return SECRET_JSON_FIELDS.every((field) => secretJsonKeys.includes(field));
    } catch {
      return false;
    }
  };

  const validateNoLeadingTrailingWhitespace = (value) => {
    if (!value || typeof value !== 'string') {
      return true;
    }
    return value === value.trim();
  };
  const noWhitespaceString = Yup.string().test(
    'no_whitespace',
    'Cannot start or end with spaces',
    validateNoLeadingTrailingWhitespace
  );
  const noWhitespaceRequiredString = noWhitespaceString.required('Required');

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        display_name: Yup.string()
          .required('Required')
          .test('is_display_name_unique', 'Subscription name must by unique', getDisplayNameValidation),
        sub_organization_ids: Yup.array().test('sub_orgs_ids', 'Required', (value) => {
          return organization.sub_organizations_enabled ? value?.length > 0 : true;
        }),
        target_type: Yup.string().oneOf(Object.keys(DATA_EXPORT.TARGET_TYPE)).required('Required'),
        user_id: Yup.string()
          .nullable()
          .when('target_type', { is: 'email', then: Yup.string().required('Required') }),
        target_configurations: Yup.object()
          .when('target_type', {
            is: 's3_bucket',
            then: Yup.object().shape({
              s3_bucket: Yup.object().shape({
                path: noWhitespaceRequiredString,
                bucket_name: noWhitespaceRequiredString,
                access_key_id: noWhitespaceRequiredString,
                access_key_secret: noWhitespaceRequiredString,
              }),
            }),
          })
          .when('target_type', {
            is: 'gcs_bucket',
            then: Yup.object().shape({
              gcs_bucket: Yup.object().shape({
                path: noWhitespaceRequiredString,
                bucket_name: noWhitespaceRequiredString,
                secret_json: noWhitespaceString.test(
                  'is_secret_json_valid',
                  'Invalid secret json format',
                  getSecretJsonValidation
                ),
              }),
            }),
          })
          .when('target_type', {
            is: 'sftp',
            then: Yup.object().shape({
              sftp: Yup.object().shape({
                host: noWhitespaceRequiredString,
                port: noWhitespaceRequiredString,
                path: noWhitespaceRequiredString,
                user: noWhitespaceRequiredString,
                password: noWhitespaceRequiredString,
              }),
            }),
          }),
        export_format: Yup.string().oneOf(Object.keys(DATA_EXPORT.EXPORT_FORMAT)).required('Required'),
        export_days: Yup.array().required('Required').min(1, 'Required'),
        export_hour: Yup.string().required('Required'),
        end_date: Yup.string()
          .nullable()
          .when('is_end_date', { is: true, then: Yup.string().required('Required') }),
        is_active: Yup.boolean().required('Required'),
        should_use_pgp_encryption: Yup.boolean(),
        pgp_encryption_public_key: Yup.string()
          .nullable()
          .when('should_use_pgp_encryption', {
            is: true,
            then: Yup.string().nullable().required('PGP public key is required when encryption is enabled'),
          }),
      })}
      enableReinitialize
      onSubmit={async (values) => {
        try {
          const target_type = values.target_type;
          if (target_type === 'email') {
            values.target_configurations = {};
          } else {
            values.user_id = undefined;
            const chosen_configuration = values.target_configurations[target_type];
            values.target_configurations = {};
            values.target_configurations[target_type] = chosen_configuration;
          }
          if (!values.is_end_date) {
            values.end_date = undefined;
          }
          if (!values.should_use_pgp_encryption) {
            values.pgp_encryption_public_key = undefined;
          }
          values.export_days.sort();

          if (values?.sub_organization_ids?.includes(SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id)) {
            values.sub_organization_ids = [];
            values.is_all_sub_orgs = true;
          }

          await onSubmit(values);
          onClose();
          mixpanel.track(
            scheduleExportToEdit
              ? MIXPANEL_EVENTS.DATA_EXPORT_SUBSCRIPTION_RULE_UPDATED
              : MIXPANEL_EVENTS.NEW_DATA_EXPORT_SUBSCRIPTION_RULE_SUBMITTED,
            {
              subscription_name: values.display_name,
              sub_organizations: values.sub_organization_ids,
              target: values.target_type,
              format: values.export_format,
              daily_repeat_on: values.export_days,
              hour: values.export_hour,
              end_date: values.end_date,
              active_subscribe: values.is_active,
            }
          );
        } catch (error) {
          await reportAxiosError(error);
        }
      }}
    >
      <ScheduledExportDialogInner onClose={onClose} scheduleExportToEdit={scheduleExportToEdit} />
    </Formik>
  );
};

ScheduledExportDialog.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  scheduledExports: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string.isRequired,
    })
  ).isRequired,
  rowIDToEdit: PropTypes.number,
};

const ScheduledExportDialogInner = ({ onClose, scheduleExportToEdit }) => {
  const { organization } = useSysconfig();
  const { classes } = useStyles();
  const { isSubmitting, handleSubmit, values } = useFormikContext();

  const { sub_organizations_enabled } = organization;

  return (
    <CardDialog
      isDialog
      title={`${scheduleExportToEdit ? 'Edit' : 'Add New'} Subscription Rule`}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      preventClose={isSubmitting}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item xs={12}>
          <AlertBanner
            title="Please Note"
            note={NON_CLAIM_COMMUNICATION_ATTENTION_NOTE}
            alertType={AlertBanner.ALERT_TYPES.INFO}
          />
        </Grid>
        <Grid item>
          <Typography display="block" variant="subtitle1">
            Subscription Settings
          </Typography>
        </Grid>
        <Grid container item className={styles.subscriptionSettings} direction="row" spacing={4}>
          <Grid item xs={6}>
            <TextFieldFormik
              id="display_name"
              label="Subscription Name"
              fullWidth
              className={classes.textField}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={6}>
            {sub_organizations_enabled && (
              <SubOrgMultiSelectWithChipsFormik
                id="sub_organization_ids"
                label="Sub Organizations"
                showOnly={isSubmitting}
                disabled={isSubmitting}
              />
            )}
          </Grid>
        </Grid>
        <TargetConfigurationSection />
        <Grid item xs={12}>
          <Typography display="block" variant="subtitle1" className={classes.h4}>
            Format
          </Typography>
          <RadioButtonGroupFormik
            id="export_format"
            direction="row"
            showOnly={isSubmitting}
            noSpacing={true}
            options={
              Object.keys(DATA_EXPORT.EXPORT_FORMAT).map((key) => ({
                text: DATA_EXPORT.EXPORT_FORMAT[key].desc,
                optionValue: DATA_EXPORT.EXPORT_FORMAT[key].key,
              })) || []
            }
          />
        </Grid>
        <ScheduleDetailsSection />
        <Grid item xs={12}>
          <div className="inline-flex items-baseline">
            <SwitchFormik
              id="include_non_claim_communications"
              label="Include communications not attached to a claim in DE files"
              disabled={isSubmitting}
            />
            <TooltipIcon
              placement="top-start"
              arrow
              title="Communications not attached to any claim may contain sub-organization specific information."
            >
              <InfoIcon className="ml-12" size={18} />
            </TooltipIcon>
          </div>
          <div className="inline-flex items-baseline">
            <SwitchFormik
              id="include_email_communication_content"
              label="Include email content in DE files"
              disabled={isSubmitting}
            />
            <TooltipIcon
              placement="top-start"
              arrow
              title="Includes the full email body in the exported EmailCommunication files. Email bodies may contain personal information. In Excel files, a body longer than 32,767 characters is cut and ends with '... [truncated]'."
            >
              <InfoIcon className="ml-12" size={18} />
            </TooltipIcon>
          </div>
          <SwitchFormik id="is_active" label="Active subscribe" disabled={isSubmitting} />
          <SwitchFormik id="should_use_pgp_encryption" label="Encrypt export with PGP" disabled={isSubmitting} />
        </Grid>
        {values.should_use_pgp_encryption && (
          <Grid item xs={12}>
            <TextFieldFormik
              id="pgp_encryption_public_key"
              label="PGP Public Key"
              fullWidth
              multiline
              rows={6}
              className={classes.textField}
              disabled={isSubmitting}
              placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----"
            />
          </Grid>
        )}

        <div className={classes.buttonsContainer}>
          <CancelButton disabled={isSubmitting} onClick={onClose} />
          <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
            {scheduleExportToEdit ? 'UPDATE' : 'SAVE'}
          </Button>
        </div>
      </Grid>
    </CardDialog>
  );
};

ScheduledExportDialogInner.propTypes = {
  onClose: PropTypes.func.isRequired,
  scheduleExportToEdit: PropTypes.object,
};

export default ScheduledExportDialog;
